import { useState } from 'react';
import { useCustom, useNavigation } from '@refinedev/core';
import { formatDate, resolveUserDisplayName } from '@/lib/utils';
import { toast } from '@/hooks/useToast';
import {
  Search,
  Mail,
  Phone,
  Hash,
  User,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';

interface LookupResult {
  id: string;
  email: string;
  phone: string | null;
  display_name: string;
  first_name: string;
  last_name: string;
  status: 'active' | 'suspended' | 'deleted';
  created_at: string;
  last_active_at: string | null;
  photos_count: number;
  matches_count: number;
  // `messages_count` removed 2026-05-11 — in-app messaging excised
  // in PR #619. Backend admin.ts still SELECTs against core.messages
  // (decays to 0 as table drains); UI was actively misleading for
  // internal compliance reviews. Backend SELECT will be removed in
  // the follow-up DB drop migration PR.
}

type SearchType = 'email' | 'phone' | 'user_id';

const searchTypeOptions: { value: SearchType; label: string; icon: React.ElementType; placeholder: string }[] = [
  { value: 'email', label: 'Email', icon: Mail, placeholder: 'user@example.com' },
  { value: 'phone', label: 'Phone', icon: Phone, placeholder: '+1234567890' },
  { value: 'user_id', label: 'User ID', icon: Hash, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
];

const statusColors = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-yellow-100 text-yellow-800',
  deleted: 'bg-red-100 text-red-800',
};

export function SupportLookupPage() {
  const { show } = useNavigation();
  const [searchType, setSearchType] = useState<SearchType>('email');
  const [searchValue, setSearchValue] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState(false);

  const { data, isLoading, isError } = useCustom<LookupResult>({
    url: '/v1/admin/support/lookup',
    method: 'get',
    config: {
      query: {
        [searchType]: searchValue,
      },
    },
    queryOptions: {
      enabled: searchSubmitted && searchValue.length > 0,
      retry: false,
    },
  });

  const user = data?.data;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      setSearchSubmitted(true);
    }
  };

  const handleReset = () => {
    setSearchValue('');
    setSearchSubmitted(false);
  };

  const currentSearchType = searchTypeOptions.find((t) => t.value === searchType);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Support Lookup</h2>
        <p className="text-muted-foreground">
          Find users by email, phone number, or user ID
        </p>
      </div>

      {/* Search Form */}
      <div className="rounded-lg border bg-card p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search Type Selection */}
          <div className="flex gap-2">
            {searchTypeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setSearchType(option.value);
                  setSearchSubmitted(false);
                }}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  searchType === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                <option.icon className="h-4 w-4" />
                {option.label}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setSearchSubmitted(false);
                }}
                placeholder={currentSearchType?.placeholder}
                className="h-12 w-full rounded-lg border bg-background pl-11 pr-4 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <button
              type="submit"
              disabled={!searchValue.trim()}
              className="h-12 rounded-lg bg-primary px-6 text-base font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Search
            </button>
            {searchSubmitted && (
              <button
                type="button"
                onClick={handleReset}
                className="h-12 rounded-lg border px-4 text-sm font-medium hover:bg-muted"
              >
                Clear
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Results */}
      {searchSubmitted && (
        <div>
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : isError ? (
            <div className="rounded-lg border border-muted bg-muted/20 p-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-4 text-lg font-medium">No user found</p>
              <p className="mt-1 text-muted-foreground">
                No user matches the {searchType.replace('_', ' ')} "{searchValue}"
              </p>
            </div>
          ) : user ? (
            <div className="rounded-lg border bg-card">
              {/* User Header */}
              <div className="flex items-center justify-between border-b p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <User className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">
                      {resolveUserDisplayName(user)}
                    </h3>
                    <p className="text-muted-foreground">{user.email}</p>
                  </div>
                  <span className={`ml-2 inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${statusColors[user.status]}`}>
                    {user.status}
                  </span>
                </div>
                <button
                  onClick={() => show('users', user.id)}
                  className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  View Full Profile
                  <ExternalLink className="h-4 w-4" />
                </button>
              </div>

              {/* User Details */}
              <div className="grid gap-6 p-6 md:grid-cols-2">
                {/* Contact Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Contact Information</h4>
                  <div className="space-y-3 rounded-lg bg-muted/50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Email</span>
                      <span className="font-mono text-sm">{user.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Phone</span>
                      <span className="font-mono text-sm">{user.phone || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">User ID</span>
                      <span className="font-mono text-xs">{user.id}</span>
                    </div>
                  </div>
                </div>

                {/* Account Stats */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Account Statistics</h4>
                  {/* Grid collapsed to 2-col 2026-05-11 — Messages stat-card
                      removed; see SupportUser interface comment for context. */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg bg-muted/50 p-4 text-center">
                      <div className="text-2xl font-bold">{user.photos_count}</div>
                      <div className="text-xs text-muted-foreground">Photos</div>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4 text-center">
                      <div className="text-2xl font-bold">{user.matches_count}</div>
                      <div className="text-xs text-muted-foreground">Matches</div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="space-y-4 md:col-span-2">
                  <h4 className="font-semibold">Timeline</h4>
                  <div className="flex gap-8 rounded-lg bg-muted/50 p-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Joined</div>
                      <div className="font-medium">{formatDate(user.created_at)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Last Active</div>
                      <div className="font-medium">
                        {user.last_active_at ? formatDate(user.last_active_at) : 'Never'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t p-6">
                <h4 className="mb-4 font-semibold">Quick Actions</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => show('users', user.id)}
                    className="rounded-lg bg-muted px-4 py-2 text-sm font-medium hover:bg-muted/80"
                  >
                    View Profile
                  </button>
                  <button
                    onClick={() => {
                      void navigator.clipboard.writeText(user.id);
                      toast({ title: 'User ID copied', description: user.id });
                    }}
                    className="rounded-lg bg-muted px-4 py-2 text-sm font-medium hover:bg-muted/80"
                  >
                    Copy User ID
                  </button>
                  <button
                    onClick={() => {
                      void navigator.clipboard.writeText(user.email);
                      toast({ title: 'Email copied', description: user.email });
                    }}
                    className="rounded-lg bg-muted px-4 py-2 text-sm font-medium hover:bg-muted/80"
                  >
                    Copy Email
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Help Text */}
      {!searchSubmitted && (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          <Search className="mx-auto h-12 w-12" />
          <p className="mt-4">Enter a search value above to find a user</p>
          <p className="mt-1 text-sm">
            You can search by email address, phone number, or user ID
          </p>
        </div>
      )}
    </div>
  );
}
